/**
 * MY MENTIONS (MENTIONS.md) — ONE shared cache of the signed-in person's unread @-mentions: the sidebar bolds a room +
 * shows its count, the Inbox lists them. Refreshes on mount, on the window event ``stepflow:mentions-changed`` (a live
 * ROOM_MENTION on MY user channel, or a room I just opened) and on tab focus. Only ever this person's — nobody else's
 * sidebar changes when I'm tagged.
 */
import { useEffect, useState } from 'react';
import { getMyMentions } from '../services/mentionsApi.js';

export const MENTIONS_CHANGED = 'stepflow:mentions-changed';
const EMPTY = { rooms: {}, total: 0, items: [] };
const store = { data: EMPTY, key: '', inflight: null, subs: new Set() };

function load(key) {
  if (!key) { store.data = EMPTY; store.subs.forEach((f) => f(EMPTY)); return Promise.resolve(EMPTY); }
  if (store.inflight) return store.inflight;
  store.key = key;
  store.inflight = getMyMentions()
    .then((d) => { store.data = { rooms: d.rooms || {}, total: d.total || 0, items: d.items || [] }; store.subs.forEach((f) => f(store.data)); return store.data; })
    .catch(() => store.data)
    .finally(() => { store.inflight = null; });
  return store.inflight;
}

export function __resetMentions() { store.data = EMPTY; store.key = ''; store.inflight = null; }

/** ``key`` = the active workspace (user+tenant); '' → nothing (signed out / no workspace). */
export default function useMyMentions(key) {
  const [data, setData] = useState(store.data);
  useEffect(() => {
    const sub = (d) => setData(d);
    store.subs.add(sub);
    load(key);
    const refresh = () => load(key);
    window.addEventListener(MENTIONS_CHANGED, refresh);
    window.addEventListener('focus', refresh);
    return () => { store.subs.delete(sub); window.removeEventListener(MENTIONS_CHANGED, refresh); window.removeEventListener('focus', refresh); };
  }, [key]);
  return data;
}
